import { EditorIcon } from "@/components/environments/EditorIcon";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Switch } from "@/components/Switch";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import { useListEditors, type PlainEditor } from "@/queries/editor-queries";
import {
    useGetOrganizationPolicies,
    useUpdateOrganizationPolicies,
    type PlainOrganizationPolicies,
} from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";

import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

type AvailableEditorsProps = {
    editable?: boolean;
};

export const AvailableEditors: FC<AvailableEditorsProps> = ({ editable = true }) => {
    const [isManageEditorsModalOpen, setIsManageEditorsModalOpen] = useState(false);

    const { data: editors, isLoading: isEditorsLoading } = useListEditors();
    const { data: organizationPolicies, isLoading: isOrganizationPoliciesLoading } = useGetOrganizationPolicies();
    const isLoading = isEditorsLoading || isOrganizationPoliciesLoading;

    const availableEditors = useMemo(() => {
        if (!!organizationPolicies?.allowedEditorIds && organizationPolicies?.allowedEditorIds.length === 0) {
            return editors?.editors || [];
        }
        return editors?.editors.filter((editor) => organizationPolicies?.allowedEditorIds.includes(editor.id)) || [];
    }, [editors, organizationPolicies]);

    return (
        <div className="flex flex-col gap-4 rounded-lg border border-border-subtle px-5 py-4">
            <div className="flex items-center justify-between">
                <Heading3>Available editors</Heading3>
                <Button
                    data-testid="manage-editors-button"
                    disabled={!editable}
                    onClick={() => setIsManageEditorsModalOpen(true)}
                    variant="secondary"
                    size="sm"
                    data-tracking-id="manage-editors-available-editors-element"
                >
                    Manage
                </Button>
            </div>
            <SkeletonBlock ready={!isLoading && availableEditors.length > 0}>
                <div className="flex flex-wrap gap-2">
                    {availableEditors.map((editor, index) => (
                        <EditorPill
                            key={`${editor.id}-${index}`}
                            editor={editor}
                            isDefault={editor.id === organizationPolicies?.defaultEditorId}
                        />
                    ))}
                </div>
            </SkeletonBlock>
            {isManageEditorsModalOpen && organizationPolicies && (
                <ManageEditorsModal
                    data-testid="manage-editors-modal"
                    onClose={() => setIsManageEditorsModalOpen(false)}
                    editors={editors?.editors || []}
                    policies={organizationPolicies}
                    editable={editable}
                />
            )}
        </div>
    );
};

const EditorPill: FC<{ editor: PlainEditor; isDefault?: boolean }> = ({ editor, isDefault }) => {
    return (
        <div
            className={cn(
                "inline-flex h-9 select-none items-center gap-2 whitespace-nowrap rounded-xl border-0.5 bg-surface-primary p-2 px-4 pb-[6.5px] pt-[5.5px] text-base font-medium",
                isDefault ? "border-border-brand" : "border-border-base",
            )}
        >
            <EditorIcon editor={editor} size="base" />
            <span>{editor.name}</span>
        </div>
    );
};

type ModalProps = {
    onClose: () => void;
    editors: PlainEditor[];
    policies: PlainOrganizationPolicies;
    editable?: boolean;
};
const ManageEditorsModal: FC<ModalProps> = ({ onClose, editors, policies, editable = true }) => {
    const [selectedEditors, setSelectedEditors] = useState<Record<string, boolean>>(() => {
        if (policies.allowedEditorIds.length > 0) {
            return policies.allowedEditorIds.reduce(
                (acc, id) => {
                    acc[id] = true;
                    return acc;
                },
                {} as Record<string, boolean>,
            );
        }
        return editors.reduce(
            (acc, editor) => {
                acc[editor.id] = true;
                return acc;
            },
            {} as Record<string, boolean>,
        );
    });

    const [defaultEditor, setDefaultEditor] = useState<string>(policies.defaultEditorId || "invalid");

    const [isDirty, setIsDirty] = useState(false);

    const isInvalidSelection = useMemo(() => {
        return !Object.entries(selectedEditors).some(([_, checked]) => checked);
    }, [selectedEditors]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const handleSave = useCallback(async () => {
        const params: PartialMessage<OrganizationPolicies> = {
            allowedEditorIds: Object.entries(selectedEditors)
                .filter(([_, checked]) => checked)
                .map(([id]) => id),
        };
        if (defaultEditor !== "invalid") {
            params.defaultEditorId = defaultEditor;
        }
        try {
            await updateOrganizationPolicies.mutateAsync(params);
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [selectedEditors, defaultEditor, updateOrganizationPolicies, onClose, toast]);

    const handleEditorSelectionChange = useCallback(
        (editorId: string, checked: boolean) => {
            setSelectedEditors((prev) => ({ ...prev, [editorId]: checked }));
            setIsDirty(true);
            if (!checked && defaultEditor === editorId) {
                const newDefaultEditor = Object.entries(selectedEditors).find(
                    ([id, checked]) => checked && id !== editorId,
                )?.[0];
                setDefaultEditor(newDefaultEditor || "invalid");
            }
            if (checked && defaultEditor === "invalid") {
                setDefaultEditor(editorId);
            }
        },
        [defaultEditor, selectedEditors],
    );

    const handleDefaultEditorChange = useCallback((editorId: string) => {
        setDefaultEditor(editorId);
        setIsDirty(true);
    }, []);

    return (
        <Dialog open onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="manage-editors-modal-content"
                data-track-location={TrackLocations.ManageEditorsModal}
            >
                <DialogHeader>
                    <DialogTitle>Available editors</DialogTitle>
                    <DialogDescription>Manage the editors available to your members</DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    {editors.map((editor, index) => (
                        <div
                            key={`${editor.id}-${index}`}
                            className="group flex h-[32px] flex-row items-center gap-4 rounded-md px-2 hover:bg-surface-muted"
                        >
                            <Switch
                                disabled={!editable}
                                state={selectedEditors[editor.id] ? "checked" : "unchecked"}
                                onToggle={(checked) => handleEditorSelectionChange(editor.id, checked)}
                                id={`editor-toggle-${index}`}
                            />
                            <div className="flex grow flex-row gap-2">
                                <EditorIcon editor={editor} size="base" />
                                <span>{editor.name}</span>
                            </div>
                            <div>
                                {defaultEditor === editor.id && <Text className="text-content-secondary">default</Text>}
                                {defaultEditor !== editor.id && selectedEditors[editor.id] && (
                                    <Button
                                        disabled={!editable}
                                        variant="ghost"
                                        size="md"
                                        className="hidden border-0 bg-none p-0 text-content-secondary hover:bg-transparent group-hover:block"
                                        onClick={() => handleDefaultEditorChange(editor.id)}
                                        data-tracking-id="make-default-manage-editors-modal"
                                    >
                                        make default
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </DialogBody>

                <DialogFooter className="items-center">
                    {isInvalidSelection && (
                        <div className="grow">
                            <Text className="text-base text-content-red">You must have 1 available editor</Text>
                        </div>
                    )}
                    <DialogClose asChild>
                        <Button type="button" variant="secondary">
                            Close
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        loading={updateOrganizationPolicies.isPending}
                        disabled={!editable || !isDirty || isInvalidSelection}
                        data-testid="save-editors-button"
                        type="button"
                        variant="primary"
                        data-tracking-id="save-editors-manage-editors-modal"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
